import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { setDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { LearnCardDestination } from "@-/dev.improve.haniverse.lib.learn.destination";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useCallback } from "react";

import { useLearnCardDestinationSettings } from "#use-learn-card-destination-settings";

/**
 * What reaching the next card of the Learn flow does to the session, whichever surface got there
 * (the deck promoting the next flashcard in its queue, or a route-driven card navigating):
 *
 *   * New Cards spends a full bar on one unit: its first new character drains review recharge to
 *     0, and the rest of the unit follows. Anything but another new card (back to a review, the
 *     summary, leaving the stack) ends that run: the toggle returns to OFF, and it is offered again
 *     only once the bar is full.
 *   * A daily review goal completed by the action that led here is celebrated.
 *
 * Returns whether it switched New Cards off, so a caller planning ahead can tell its own change
 * from the learner's.
 */
export function useLearnCardArrival(): (kind: LearnCardDestination["kind"]) => {
  endedNewCardsRun: boolean;
} {
  const settingsRef = useLatest(useLearnCardDestinationSettings());
  const [dailyReviewCompletedRef, setReviewCelebration] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.dailyReviewCompletedRef,
    (c) => c.setReviewCelebration,
  );
  return useCallback(
    (kind: LearnCardDestination["kind"]) => {
      const endedNewCardsRun =
        kind !== "lesson" && kind !== "slide" && settingsRef.current.reviewRechargeNewCardsEnabled;
      if (endedNewCardsRun) {
        setDeviceSetting("reviewRechargeNewCardsEnabled", false);
      }
      if (dailyReviewCompletedRef.current) {
        dailyReviewCompletedRef.current = false;
        setReviewCelebration((value) => value + 1);
      }
      return { endedNewCardsRun };
    },
    [dailyReviewCompletedRef, setReviewCelebration, settingsRef],
  );
}
